import { computed, ref } from 'vue'
import { getConfig, getConfigValue } from '@/api/config'
import { isUnifiedOrderVersionValue } from '@/composables/useUnifiedOrderVersion'

/** 配置中心：询价统一详情版本开关（legacy | v2-beta） */
export const INQUIRY_UNIFIED_VERSION_CONFIG = 'inquiry.unified.version.switch'
export const INQUIRY_UNIFIED_VERSION_LEGACY = 'legacy'
export const INQUIRY_UNIFIED_VERSION_V2 = 'v2-beta'

const inquiryUnifiedVersionEnabled = ref(false)
const configLoaded = ref(false)
let pending: Promise<boolean> | null = null

async function fetchInquiryUnifiedVersionRaw(): Promise<unknown> {
  try {
    const detail = await getConfig({ configName: INQUIRY_UNIFIED_VERSION_CONFIG })
    if (detail?.currentValue !== null && detail?.currentValue !== undefined && String(detail.currentValue).trim()) {
      return detail.currentValue
    }
    if (detail?.configValue !== null && detail?.configValue !== undefined && String(detail.configValue).trim()) {
      return detail.configValue
    }
  } catch (error) {
    console.warn('[CT_INQUIRY_UNIFIED] getConfig failed, fallback getConfigValue', error)
  }
  return getConfigValue(INQUIRY_UNIFIED_VERSION_CONFIG)
}

/**
 * 从配置中心加载询价统一详情版本（CCTOWER-2720）。
 * 默认 legacy；currentValue = v2-beta 时走视图模板标量基本信息。
 */
export async function loadInquiryUnifiedVersionConfig(force = false): Promise<boolean> {
  if (configLoaded.value && !force) {
    return inquiryUnifiedVersionEnabled.value
  }
  if (pending && !force) {
    return pending
  }

  pending = fetchInquiryUnifiedVersionRaw()
    .then(raw => {
      inquiryUnifiedVersionEnabled.value = isUnifiedOrderVersionValue(raw)
      configLoaded.value = true
      if (import.meta.env.DEV) {
        console.log('[CT_INQUIRY_UNIFIED]', { raw, enabled: inquiryUnifiedVersionEnabled.value })
      }
      return inquiryUnifiedVersionEnabled.value
    })
    .catch(error => {
      console.warn('[CT_INQUIRY_UNIFIED] load config failed, fallback to legacy', error)
      inquiryUnifiedVersionEnabled.value = false
      configLoaded.value = true
      return false
    })
    .finally(() => {
      pending = null
    })

  return pending
}

export function resetInquiryUnifiedVersionConfigCache(): void {
  configLoaded.value = false
  inquiryUnifiedVersionEnabled.value = false
  pending = null
}

export function useInquiryUnifiedVersion() {
  if (!configLoaded.value && !pending) {
    void loadInquiryUnifiedVersionConfig()
  }

  const isInquiryUnifiedV2 = computed(() =>
    configLoaded.value && inquiryUnifiedVersionEnabled.value
  )

  return {
    isInquiryUnifiedV2,
    configLoaded,
    loadInquiryUnifiedVersionConfig
  }
}

export function useInquiryUnifiedVersionState() {
  return {
    isInquiryUnifiedVersionEnabled: inquiryUnifiedVersionEnabled,
    isInquiryUnifiedVersionConfigLoaded: configLoaded
  }
}
